import { useEffect, useState } from "react";
import axios from "../api/axios";

/**
 * Fetches just the total `count` from a paginated list endpoint (the same
 * DRF-style {count, results} response CustomDataGrid/useCRUD consume),
 * without pulling in the actual rows. Meant for showing an item-count badge
 * next to a tab title even while that tab's own grid isn't mounted.
 *
 * Refetches on mount and whenever `url`/`enabled` change (e.g. the parent
 * entity id changes) - same timing as the existing unread-messages badge on
 * the Job view, not a live subscription to list mutations elsewhere on the
 * page.
 *
 * Pass `enabled=false` (e.g. the viewer lacks the relevant permission) to
 * skip the request entirely; the hook then resolves to `undefined`, and
 * TabCountBadge renders the plain label with no badge.
 */
export const useListCount = (url: string | null, enabled: boolean = true): number | undefined => {
  const [count, setCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!url || !enabled) {
      setCount(undefined);
      return;
    }

    let isCurrent = true;
    axios
      .get(url, { params: { page_size: 1 } })
      .then(({ data }) => {
        if (isCurrent) {
          setCount(typeof data?.count === "number" ? data.count : undefined);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setCount(undefined);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [url, enabled]);

  return count;
};
